import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useTheme } from '../../providers/ThemeProvider';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';

import { CALIBRATION_SENSITIVE_CATEGORIES } from './types';
import { isImageFile, is3DFile, isCBCTFile } from './fileUtils';
import { PhotoCategoryDialog, ModelCategoryDialog } from './CategoryDialogs';
import GalleryStrip from './GalleryStrip';
import StepSidebar from './StepSidebar';
import ViewerPanel from './ViewerPanel';
import PatientCaseService from '../../services/PatientCaseService';

function resetCalibrationIfStale(files, replacedCategory, onReset) {
    if (!CALIBRATION_SENSITIVE_CATEGORIES.has(replacedCategory)) return files;
    const hasCalibration = files.some(f => f.calibrationProject);
    if (!hasCalibration) return files;
    onReset?.();
    return files
        .filter(f => !(f.category === 'Calibration' && f.calibrationProject))
        .map(f => f.calibrationProject ? { ...f, calibrationProject: undefined, isDirty: true } : f);
}

const NewPatient = ({ onPatientSaved, initialPatient }) => {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const isDark = theme === 'dark';
    const fileInputRef = useRef(null);
    const editingIdRef = useRef(initialPatient?.id || null);

    const [activeStep, setActiveStep] = useState('digitalRecords');
    const [patientName, setPatientName] = useState(initialPatient?.name || '');
    const [files, setFiles] = useState(() => {
        if (!initialPatient?.files) return [];
        return initialPatient.files;
    });
    const [isDragging, setIsDragging] = useState(false);

    // Image categorization popup state
    const [pendingImages, setPendingImages] = useState([]);
    const [currentPendingIndex, setCurrentPendingIndex] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);

    // 3D model categorization popup state
    const [pendingModels, setPendingModels] = useState([]);
    const [currentModelIndex, setCurrentModelIndex] = useState(0);
    const [modelPopupOpen, setModelPopupOpen] = useState(false);

    // Viewer state
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedPair, setSelectedPair] = useState(null);

    const isViewerOpen = activeStep === 'editFiles' && (selectedFile !== null || selectedPair !== null);
    const isCalibrating = activeStep === 'calibrate';
    const isFullscreen = isViewerOpen || isCalibrating;

    const [savingDoc, setSavingDoc] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [savePatientName, setSavePatientName] = useState('');

    // Register project guard so NavigationGuard can detect unsaved data
    useEffect(() => {
        const project = {
            hasData: () => files.length > 0,
            download: async () => {},
        };
        window.__smilefy_project = project;
        return () => { if (window.__smilefy_project === project) delete window.__smilefy_project; };
    }, [files]);

    const handleSaveDocumentation = useCallback(async () => {
        if (savingDoc || files.length === 0) return;
        setSavePatientName(patientName || initialPatient?.name || '');
        setSaveDialogOpen(true);
    }, [savingDoc, files, patientName, initialPatient]);

    const handleConfirmSave = useCallback(async () => {
        setSaveDialogOpen(false);
        setSavingDoc(true);
        try {
            const now = new Date();
            const formatted = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

            // Find first image preview to use as thumbnail
            const firstImage = files.find(f => f.isImage && f.preview);

            // Mirror into the app's My Patients using the app's exact save logic
            // (POST /patient-service-v2/cases → file uploads). source:'drive' tags
            // the patient so the app shows the SmileFy Drive chip.
            let appResult = null;
            try {
                appResult = await PatientCaseService.savePatientToApp({
                    patientName: savePatientName || 'Unnamed Patient',
                    files,
                });
            } catch (e) {
                console.error('App mirror failed:', e);
                alert(t('patient.appMirrorFailed', 'Saved locally, but syncing to the app failed.'));
            }

            const patient = {
                id: editingIdRef.current || appResult?.patientId || Date.now(),
                appPatientId: appResult?.patientId,
                appCaseId: appResult?.caseId,
                name: savePatientName || 'Unnamed Patient',
                photo: firstImage?.preview || initialPatient?.photo || null,
                fileCount: files.length,
                files: files,
                createdAt: initialPatient?.createdAt || formatted,
                lastOpened: formatted,
            };

            onPatientSaved?.(patient);
            // Reset form
            setFiles([]);
            setPatientName('');
            setSavePatientName('');
            setActiveStep('digitalRecords');
            setSelectedFile(null);
            setSelectedPair(null);
            editingIdRef.current = null;
        } finally {
            setSavingDoc(false);
        }
    }, [files, savePatientName, onPatientSaved, initialPatient]);

    /* ── File handling ── */

    const addFiles = useCallback((incoming) => {
        const images = [];
        const models = [];
        const direct = [];

        for (const f of incoming) {
            if (isImageFile(f)) {
                images.push(f);
            } else if (is3DFile(f)) {
                models.push(f);
            } else if (isCBCTFile(f)) {
                direct.push({ file: f, preview: '', isImage: false, is3D: false, isCBCT: true, category: 'CBCT' });
            } else {
                direct.push({ file: f, preview: '', isImage: false, is3D: false, isCBCT: false });
            }
        }

        if (direct.length > 0) setFiles(prev => [...prev, ...direct]);

        if (images.length > 0) {
            setPendingImages(images);
            setCurrentPendingIndex(0);
            setPopupOpen(true);
        }

        if (models.length > 0) {
            setPendingModels(models);
            setCurrentModelIndex(0);
            if (images.length === 0) setModelPopupOpen(true);
        }
    }, []);

    const handleRemoveFile = (index) => {
        setFiles(prev => {
            const removed = prev[index];
            if (removed.preview) URL.revokeObjectURL(removed.preview);
            if (selectedFile && selectedFile === removed) {
                setSelectedFile(null);
                if (removed.calibrationProject && activeStep === 'calibrate') {
                    setActiveStep('digitalRecords');
                }
            }
            if (selectedPair && (removed.file === selectedPair.upper || removed.file === selectedPair.lower)) setSelectedPair(null);
            return prev.filter((_, i) => i !== index);
        });
    };

    const addScreenshotToGallery = useCallback((dataUrl) => {
        fetch(dataUrl).then(r => r.blob()).then(blob => {
            const file = new File([blob], `screenshot_${Date.now()}.png`, { type: 'image/png' });
            const preview = URL.createObjectURL(blob);
            setFiles(prev => [...prev, { file, preview, isImage: true, is3D: false, isCBCT: false, isScreenshot: true, category: 'Others', isDirty: true }]);
        });
    }, []);

    /* ── Category selection handlers ── */

    const handlePhotoCategorySelect = (category) => {
        const file = pendingImages[currentPendingIndex];
        const preview = URL.createObjectURL(file);
        setFiles(prev => {
            const updated = [...prev, { file, preview, isImage: true, is3D: false, isCBCT: false, category, isDirty: true }];
            return resetCalibrationIfStale(updated, category, () =>
                alert(t('patient.calibrationResetReplaced')));
        });

        const nextIndex = currentPendingIndex + 1;
        if (nextIndex < pendingImages.length) {
            setCurrentPendingIndex(nextIndex);
        } else {
            setPopupOpen(false);
            setPendingImages([]);
            setCurrentPendingIndex(0);
            if (pendingModels.length > 0) setModelPopupOpen(true);
        }
    };

    const handleModelCategorySelect = (category) => {
        const file = pendingModels[currentModelIndex];
        setFiles(prev => {
            const updated = [...prev, { file, preview: '', isImage: false, is3D: true, isCBCT: false, category, isDirty: true }];
            return resetCalibrationIfStale(updated, category, () =>
                alert(t('patient.calibrationResetReplaced')));
        });

        const nextIndex = currentModelIndex + 1;
        if (nextIndex < pendingModels.length) {
            setCurrentModelIndex(nextIndex);
        } else {
            setModelPopupOpen(false);
            setPendingModels([]);
            setCurrentModelIndex(0);
        }
    };

    /* ── Drag & drop ── */

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) addFiles(droppedFiles);
    };
    const handleFileSelect = (e) => {
        if (e.target.files) addFiles(Array.from(e.target.files));
        e.target.value = '';
    };

    /* ── Thumbnail clicks ── */

    const handleThumbClick = (pf) => {
        setSelectedPair(null);
        setSelectedFile(pf);
        if (pf.calibrationProject) {
            setActiveStep('calibrate');
        } else if (activeStep !== 'editFiles') {
            setActiveStep('editFiles');
        }
    };

    const handlePairClick = (upper, lower, label) => {
        setSelectedFile(null);
        setSelectedPair({ upper: upper?.file, lower: lower?.file, label: label || '3D Models' });
        if (activeStep !== 'editFiles') setActiveStep('editFiles');
    };

    /* ── Render ── */

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', minHeight: '100%' }}>
            {/* Main content area */}
            <Box sx={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                px: 3, py: isFullscreen ? 2 : 4,
                maxWidth: isFullscreen ? '100%' : 720,
                mx: 'auto', width: '100%',
            }}>
                {/* Header (hidden during fullscreen views) */}
                {!isFullscreen && (
                    <>
                        <Typography variant="h5" sx={{
                            fontWeight: 600, mb: 1,
                            fontFamily: '"SF Pro Display", -apple-system, "Helvetica Neue", sans-serif',
                            color: 'var(--text-1)',
                        }}>
                            {initialPatient ? `${t('patient.editingPrefix')} ${initialPatient.name}` : t('patient.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 4, color: 'var(--text-2)' }}>
                            {initialPatient
                                ? t('patient.filesAndMore', { count: files.length })
                                : t('patient.subtitle')}
                        </Typography>
                        <TextField
                            value={patientName}
                            onChange={e => setPatientName(e.target.value)}
                            placeholder={t('patient.namePlaceholder')}
                            variant="outlined"
                            fullWidth
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                                    '& fieldset': { borderColor: '#1976d2' },
                                    '&:hover fieldset': { borderColor: '#1976d2' },
                                    '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                                },
                                '& .MuiInputBase-input': { color: 'var(--text-1)' },
                            }}
                        />
                    </>
                )}

                {/* Main view area */}
                {isCalibrating ? (
                    <Box sx={{
                        width: '100%',
                        height: files.length > 0 ? 'calc(100vh - 310px)' : 'calc(100vh - 160px)',
                        borderRadius: 1.5, overflow: 'hidden', bgcolor: 'var(--bg-base)',
                        border: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Typography sx={{ color: 'var(--text-2)', fontSize: 15 }}>
                            {t('patient.calibrationComingSoon')}
                        </Typography>
                    </Box>
                ) : isViewerOpen ? (
                    <ViewerPanel
                        selectedFile={selectedFile}
                        selectedPair={selectedPair}
                        hasFiles={files.length > 0}
                        onClose={() => { setSelectedFile(null); setSelectedPair(null); }}
                        onScreenshot={addScreenshotToGallery}
                        onUpdateFile={(pf, newSrc) => {
                            setFiles(prev => {
                                const updated = prev.map(f => f === pf ? { ...f, preview: newSrc, isDirty: true } : f);
                                const cat = pf.category;
                                return resetCalibrationIfStale(updated, cat, () =>
                                    alert(t('patient.calibrationResetEdited')));
                            });
                        }}
                    />
                ) : (
                    <Box
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{
                            position: 'relative',
                            width: '100%', minHeight: 220,
                            border: `2px dashed ${isDragging ? '#42d4a8' : 'var(--border-h)'}`,
                            borderRadius: 1.5,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            gap: 1.5, cursor: 'pointer',
                            bgcolor: isDragging ? 'rgba(66,212,168,0.08)' : 'var(--bg-input)',
                            transition: 'all 0.2s ease',
                            WebkitTapHighlightColor: 'transparent',
                            touchAction: 'manipulation',
                            '@media (hover: hover)': {
                                '&:hover': { borderColor: '#42d4a8', bgcolor: 'rgba(66,212,168,0.06)' },
                            },
                        }}
                    >
                        <input ref={fileInputRef} type="file" multiple onClick={(e) => e.stopPropagation()} onChange={handleFileSelect}
                            style={{
                                position: 'absolute', top: 0, left: 0,
                                width: '100%', height: '100%',
                                opacity: 0, cursor: 'pointer', zIndex: 2,
                            }} />
                        <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? '#42d4a8' : 'var(--text-2)' }} />
                        <Typography variant="body1" sx={{ color: 'var(--text-1)', fontWeight: 500 }}>
                            {t('patient.dropFiles')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--text-3)' }}>
                            {t('patient.orBrowse')}
                        </Typography>
                    </Box>
                )}

                {/* Gallery strip */}
                {files.length > 0 && (
                    <GalleryStrip
                        files={files}
                        onThumbClick={handleThumbClick}
                        onPairClick={handlePairClick}
                        onRemoveFile={handleRemoveFile}
                    />
                )}

                {/* Category dialogs */}
                <PhotoCategoryDialog
                    open={popupOpen}
                    pendingImages={pendingImages}
                    currentIndex={currentPendingIndex}
                    onSelect={handlePhotoCategorySelect}
                    onClose={() => { setPopupOpen(false); setPendingImages([]); }}
                />
                <ModelCategoryDialog
                    open={modelPopupOpen}
                    pendingModels={pendingModels}
                    currentIndex={currentModelIndex}
                    onSelect={handleModelCategorySelect}
                    onClose={() => { setModelPopupOpen(false); setPendingModels([]); }}
                />
            </Box>

            {/* Save Patient — fixed bottom right */}
            <Button
                variant="contained"
                startIcon={savingDoc
                    ? <CircularProgress size={18} thickness={4} sx={{ color: '#fff' }} />
                    : <DescriptionIcon />
                }
                disabled={savingDoc || files.length === 0}
                onClick={handleSaveDocumentation}
                sx={{
                    position: 'fixed',
                    bottom: files.length > 0 ? 16 : 24,
                    right: 24,
                    zIndex: 1300,
                    textTransform: 'none',
                    minWidth: 200,
                }}
            >
                {savingDoc ? t('patient.saving') : t('patient.savePatient')}
            </Button>

            {/* Save Patient Dialog */}
            <Dialog
                open={saveDialogOpen}
                onClose={() => setSaveDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ color: 'var(--text-1)', fontWeight: 600, fontSize: 18 }}>
                    {t('patient.saveDialogTitle')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label={t('patient.patientNameLabel')}
                        placeholder={t('patient.patientNamePlaceholder')}
                        value={savePatientName}
                        onChange={e => setSavePatientName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleConfirmSave(); }}
                        variant="outlined"
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                color: 'var(--text-1)',
                                '& fieldset': { borderColor: 'var(--border-h)' },
                                '&:hover fieldset': { borderColor: '#7c6af7' },
                                '&.Mui-focused fieldset': { borderColor: '#7c6af7' },
                            },
                            '& .MuiInputLabel-root': { color: 'var(--text-3)' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#7c6af7' },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setSaveDialogOpen(false)}
                        sx={{ color: 'var(--text-3)', textTransform: 'none' }}
                    >
                        {t('patient.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmSave}
                        startIcon={<DescriptionIcon />}
                        sx={{ textTransform: 'none', minWidth: 120 }}
                    >
                        {t('patient.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NewPatient;
